import { ISuggestion } from '../context/SuggestionsContext';

/** `BlockingScreen` value that locks the home page immediately. */
export const BLOCKED_SCREEN = 'Blocked';

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const parseCreatedDate = (suggestion: ISuggestion): Date | null => {
  const raw = suggestion.SuggestionDate ?? suggestion.suggestiondate;
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Local calendar date on which the suggestion starts blocking the screen, or null when it carries
 * no usable day rule. The rule is `BlockAfterXDays` (must be a finite number > 0 — a stamped 0 or
 * negative is treated as "no rule", never as "block now") counted from `SuggestionDate` at
 * calendar-day granularity, so X = 3 on a Monday suggestion blocks from Thursday 00:00 local.
 * An unparseable or missing created date also yields null (fail open).
 */
export function getBlockAfterDate(suggestion: ISuggestion): Date | null {
  const days = suggestion.BlockAfterXDays;
  if (typeof days !== 'number' || !Number.isFinite(days) || days <= 0) return null;
  const created = parseCreatedDate(suggestion);
  if (!created) return null;
  const blockAfter = startOfDay(created);
  blockAfter.setDate(blockAfter.getDate() + Math.floor(days));
  return blockAfter;
}

/**
 * True when the suggestion must lock the home page: either `BlockingScreen` is `'Blocked'`
 * or its block-after date is today or earlier. `now` is injectable for tests.
 */
export function isBlockingSuggestion(suggestion: ISuggestion, now: Date = new Date()): boolean {
  if (suggestion.BlockingScreen === BLOCKED_SCREEN) return true;
  const blockAfter = getBlockAfterDate(suggestion);
  if (!blockAfter) return false;
  return startOfDay(now).getTime() >= blockAfter.getTime();
}

/** Card hint for a suggestion that is not blocking yet, e.g. `Becomes blocking on 10/20/2026`. */
export function formatBlockAfterLabel(blockAfter: Date): string {
  const formatted = blockAfter.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  return `Becomes blocking on ${formatted}`;
}
